// Utilitaires de la vue calendrier des tâches (WKF-013 §7.2bis).
// Maths de dates pures (date-fns) + garde de déplaçabilité. Aucune dépendance UI.

import {
    addDays,
    eachDayOfInterval,
    endOfMonth,
    endOfWeek,
    format,
    startOfDay,
    startOfMonth,
    startOfWeek,
} from 'date-fns'
import { enUS, fr, type Locale } from 'date-fns/locale'

import type { Notification } from '@/hooks/useNotifications.types'

export type CalendarView = 'month' | 'week' | 'day'

/** Clé de jour stable, indépendante du fuseau d'affichage (identifiant droppable). */
export function dayKey(d: Date): string {
    return format(d, 'yyyy-MM-dd')
}

export function localeFor(lang: string): Locale {
    return lang.startsWith('en') ? enUS : fr
}

/** 1re jour de semaine selon la locale (lundi en FR, dimanche en EN). */
function weekStartsOn(locale: Locale): 0 | 1 {
    return (locale.options?.weekStartsOn ?? 1) as 0 | 1
}

/** Fenêtre `[from, to)` (bornes Date) couverte par la vue courante — sert AUSSI
 *  de plage de requête serveur. Le mois s'étend aux semaines complètes de la
 *  grille (jours débordants inclus). */
export function viewRange(view: CalendarView, anchor: Date, locale: Locale): { from: Date; to: Date } {
    const wso = weekStartsOn(locale)
    if (view === 'day') {
        const from = startOfDay(anchor)
        return { from, to: addDays(from, 1) }
    }
    if (view === 'week') {
        const from = startOfWeek(anchor, { weekStartsOn: wso })
        return { from, to: addDays(from, 7) }
    }
    // month : grille de semaines complètes. `endOfWeek` renvoie 23:59:59.999 —
    // on ramène à minuit exclusif (sinon la plage déborde d'un jour hors grille :
    // sur-fetch + `truncated` faux-positif).
    const from = startOfWeek(startOfMonth(anchor), { weekStartsOn: wso })
    const to = startOfDay(addDays(endOfWeek(endOfMonth(anchor), { weekStartsOn: wso }), 1))
    return { from, to }
}

/** Jours affichés dans la vue (grille mois = multiple de 7 ; semaine = 7 ; jour = 1). */
export function viewDays(view: CalendarView, anchor: Date, locale: Locale): Date[] {
    const { from, to } = viewRange(view, anchor, locale)
    return eachDayOfInterval({ start: from, end: addDays(to, -1) })
}

/** En-têtes de colonnes (jours de semaine) localisés — vues mois/semaine. */
export function weekdayHeaders(locale: Locale): string[] {
    const wso = weekStartsOn(locale)
    const base = startOfWeek(new Date(2021, 5, 7), { weekStartsOn: wso }) // lundi 7 juin 2021
    return Array.from({ length: 7 }, (_, i) => format(addDays(base, i), 'EEEEEE', { locale }))
}

/** Regroupe les tâches par clé de jour, triées par heure d'échéance croissante. */
export function groupByDay(items: Notification[]): Map<string, Notification[]> {
    const map = new Map<string, Notification[]>()
    for (const n of items) {
        if (!n.due_date) continue
        const key = dayKey(new Date(n.due_date))
        const arr = map.get(key)
        if (arr) arr.push(n)
        else map.set(key, [n])
    }
    for (const arr of map.values()) {
        // Tri par instant (robuste aux offsets de fuseau, contrairement au tri de chaînes ISO).
        arr.sort((a, b) => new Date(a.due_date!).getTime() - new Date(b.due_date!).getTime())
    }
    return map
}

// Rang de priorité pour l'affichage (URGENT d'abord).
const PRIORITY_RANK: Record<Notification['priority'], number> = { URGENT: 0, HIGH: 1, NORMAL: 2 }

/** Ordonne les tâches d'un jour pour la vue MOIS : en retard d'abord, puis par
 *  priorité (URGENT→HIGH→NORMAL), puis heure croissante. Ainsi les pastilles
 *  visibles ne cachent jamais un urgent de l'après-midi derrière des tâches du matin. */
export function orderForMonth(tasks: Notification[]): Notification[] {
    return [...tasks].sort((a, b) => {
        if (a.is_overdue !== b.is_overdue) return a.is_overdue ? -1 : 1
        const pr = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority]
        if (pr !== 0) return pr
        return new Date(a.due_date!).getTime() - new Date(b.due_date!).getTime()
    })
}

/** Une tâche masquée (débordement « +N ») est-elle urgente ou en retard ? */
export function hasCriticalHidden(hidden: Notification[]): boolean {
    return hidden.some((n) => n.is_overdue || n.priority === 'URGENT')
}

/** Reporte une échéance sur un autre JOUR en conservant l'heure d'origine
 *  (ou 09:00 par défaut si la tâche n'avait pas d'heure). Retourne un ISO. */
export function applyDayKeepingTime(originalIso: string | null, targetDayKey: string): string {
    const [y, m, d] = targetDayKey.split('-').map(Number)
    const base = originalIso ? new Date(originalIso) : null
    const next = new Date(y, m - 1, d, base ? base.getHours() : 9, base ? base.getMinutes() : 0, 0, 0)
    return next.toISOString()
}

/** Une tâche est-elle repositionnable par glisser-déposer ? Règle STRICTEMENT
 *  IDENTIQUE à l'éditabilité du dialogue de détail (`TaskDetailDialog`, WKF-013
 *  §7.4) et au garde backend `update_task` : tâche `manual` non `IGNORED`, avec
 *  `tasks:update`, dont je suis destinataire OU assigneur. Les alertes/transferts
 *  reçus restent affichés mais figés (même divergence que le dialogue). */
export function canReschedule(
    n: Notification,
    opts: { userId: string | undefined; canUpdate: boolean },
): boolean {
    return Boolean(
        opts.canUpdate &&
            n.source_type === 'manual' &&
            n.status !== 'IGNORED' &&
            opts.userId &&
            (n.recipient_id === opts.userId || n.assigned_by?.id === opts.userId),
    )
}
